import S, { DataSignal } from 's-js';
import { IAjax } from 'lib/Ajax';
import { AutoSaver, SaveDraftObject } from 'lib/autoSave';
import { DraftObject } from 'lib/draftSignals';
import { Mutex } from 'lib/mutex';
import { ValidationWatcher } from 'lib/validationWatcher';

import { BerthReservation } from 'data/models/BerthReservation';
import { CancelReservationsRequest } from 'data/models/CancelReservationsRequest';
import { IssueType } from 'data/models/IssueType';
import { PortCallRights } from 'server/enums/Rights';

import { Entrypoints } from 'core/app/entrypoints';

export interface BerthAssignmentDetailsActionsTabParams {
    BerthReservation : BerthReservation;
    DismissDetails : () => void;
    PCRights : () => PortCallRights;
    /**
     * A mutex to prevent overlapping calls to save the given reservation or its child entities.
     */
    SaveMutex : Mutex;
}

enum BerthAssignmentAction {
    CancelReservation = 'cancel-reservation'
}

export type BerthAssignmentDetailsActionsTab = ReturnType<typeof BerthAssignmentDetailsActionsTab>;
export function BerthAssignmentDetailsActionsTab(
    ajax : IAjax,
    entrypoints : Entrypoints,
    {
        BerthReservation,
        DismissDetails,
        PCRights,
        SaveMutex
    } : BerthAssignmentDetailsActionsTabParams
) {
    const
        selectedAction = S.value<BerthAssignmentAction | null>(null),
        actionIsSelected = (action : BerthAssignmentAction) : DataSignal<boolean> => (val? : boolean) => {
            if (val === undefined) {
                return selectedAction() === action;
            } else {
                const isSelected = S.sample(selectedAction) === action;
                if (val !== isSelected) selectedAction(val ? action : null);
                return val;
            }
        },
        CancelReservation = CancelReservationAction(
            BerthReservation,
            ajax,
            entrypoints,
            DismissDetails,
            PCRights,
            SaveMutex,
            actionIsSelected(BerthAssignmentAction.CancelReservation)
        ),
        RaftReservation = RaftReservationAction(
            BerthReservation,
            ajax,
            PCRights,
            SaveMutex
        ),
        HasAnyAvailableActions = () =>
            CancelReservation.IsAvailable()
         || RaftReservation.IsAvailable(),
        Loading = () =>
            CancelReservation.IsPerforming()
         || RaftReservation.IsSaving();

    return {
        CancelReservation,
        RaftReservation,
        HasAnyAvailableActions,
        Loading
    };
}

export type CancelReservationAction = ReturnType<typeof CancelReservationAction>;
function CancelReservationAction(
    BerthReservation : BerthReservation,
    ajax : IAjax,
    entrypoints : Entrypoints,
    DismissDetails : () => void,
    PCRights : () => PortCallRights,
    saveMutex : Mutex,
    IsSelected : DataSignal<boolean>
) {
    const
        client = ajax.Url<CancelReservationsRequest, unknown>(entrypoints.CancelReservations, { useCache: true }),
        Req = S(() => IsSelected() ? new CancelReservationsRequest({
            ReservationIds: [BerthReservation.Id()]
        }) : null),
        IsAvailable = () => (PCRights() & PortCallRights.Cancel) !== 0,
        IsPerforming = S.value(false);

    return {
        BerthReservation,
        IsSelected,
        Req,
        IsAvailable,
        IsPerforming: () => IsPerforming(),
        Perform
    };

    async function Perform() {
        const req = Req();
        if (!req || IsPerforming()) return;
        IsPerforming(true);
        try {
            const guard = await saveMutex.acquire();
            try {
                await client.Post(req);
                DismissDetails();
            } finally {
                guard.release();
            }
        } finally {
            IsPerforming(false);
        }
    }
}

export type RaftReservationAction = ReturnType<typeof RaftReservationAction>;
function RaftReservationAction(
    BerthReservation : BerthReservation,
    ajax : IAjax,
    PCRights : () => PortCallRights,
    saveMutex : Mutex
) {
    const
        draft = DraftObject(BerthReservation, ['Rafting']),
        client = ajax.Url<BerthReservation, unknown>(() => BerthReservation.Url() ?? '', { useCache: true }),
        Validation = ValidationWatcher(client),
        HasRaftableOverlaps = BerthReservation.Issues.some(i => i.Type() === IssueType.ReservationOverlapsOther),
        IsAvailable = () =>
            (PCRights() & PortCallRights.EditRafting) !== 0 &&
            (BerthReservation.Rafting() || HasRaftableOverlaps()),
        IsSaving = S.value(false);

    AutoSaver(draft.methods.onWrite, async () => {
        IsSaving(true);
        try {
            const guard = await saveMutex.acquire();
            try {
                await SaveDraftObject(draft, client);
            } finally {
                guard.release();
            }
        } finally {
            IsSaving(false);
        }
    }, 0);

    return {
        Persisted: BerthReservation,
        Draft: draft.object,
        Validation,
        IsAvailable,
        IsSaving: () => IsSaving(),
        ToggleRafting
    };

    function ToggleRafting() {
        // Toggle means set to the opposite of the current PERSISTED state, so that calling Toggle again before
        // changes have been persisted has no effect.
        S.sample(() => draft.object.Rafting(!BerthReservation.Rafting()));
    }
}



// WEBPACK FOOTER //
// ./apps/portcall/berthassignmentdetails/actions.ts